/**
 * PortlessBatchGateSection — per-location "don't batch without a Portless label".
 *
 * At a floor that ships via Portless, an order cannot leave the building until
 * Portless has issued a cross-dock label. Without this gate those orders are
 * picked, printed and binned anyway, and only surface when the floor runs out of
 * bins (2026-07-27: twenty orders, thirty-six bins).
 *
 * With it on, an order whose Pipe17 label has not been issued yet
 * (`portless_label_ready_at` unstamped by the 10-min prefetch cron) is hidden
 * from Ready to Batch and rejected by batch creation. It stays fully visible in All Orders with an
 * amber "Portless label not ready" badge, and returns to Ready automatically the
 * moment the tag lands — there is nothing for an operator to clear.
 *
 * Default OFF. This changes what a live floor is allowed to batch, so it is
 * opted into per location rather than acquired on deploy. The toggle is the
 * single no-deploy kill switch. The tag list below does NOT drive the gate —
 * rev2: `Portless_Ready` etc mark "this is a Portless cross-dock order" (they
 * trigger the push into Portless's system, per Portless ops guidance), so they
 * classify a blocked order (not-yet-pushed vs awaiting-Portless) for the
 * Issues list and Slack digest, and never gate anything.
 *
 * Mirrors OrderHoldSettingsSection exactly (embedded + locationId props,
 * getLocationSettings query, per-row variables-keyed pending spinner, toast +
 * refetch, Switch per location) and adds a tag editor for the readiness
 * vocabulary, since a second client at a shared floor may tag differently in its
 * own Shopify.
 *
 * Only the organisation that OWNS the location may change this — it is a floor
 * rule, and the underlying RPC matches on the owning org, so a client-org
 * coordinator would otherwise save nothing and be told it worked.
 */
import { useRef, useState } from 'react';
import {
  PORTLESS_DIGEST_INTERVALS,
  PORTLESS_DIGEST_TIME_SLOTS,
  PORTLESS_DIGEST_MAX_TIMES,
  type PortlessDigestSchedule,
} from '@threadheads/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { NoteLine } from '@/components/shared/NoteLine';
import { PackageX, Loader2, AlertTriangle, RefreshCw, X, Plus } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { SlackMentionPicker } from '@/components/SlackMentionPicker';
import { COMMON_TIMEZONES, gmtOffsetLabel } from '@/lib/timezones';
import { toast } from 'sonner';

/** Mirrors DEFAULT_PORTLESS_IDENTIFICATION_TAGS in the backend gate module. */
const DEFAULT_IDENTIFICATION_TAGS = ['portless_ready'];

const intervalLabel = (m: number): string =>
  m === 30
    ? 'Every 30 minutes'
    : m === 60
      ? 'Every hour'
      : m === 90
        ? 'Every 90 minutes'
        : m === 1440
          ? 'Once a day'
          : `Every ${m / 60} hours`;

/** Band the 48 half-hour slots so the add-time dropdown scans by day-part. */
const SLOT_BANDS: Array<{ label: string; match: (hour: number) => boolean }> = [
  { label: 'Morning', match: (h) => h >= 6 && h < 12 },
  { label: 'Afternoon', match: (h) => h >= 12 && h < 18 },
  { label: 'Evening', match: (h) => h >= 18 },
  { label: 'Overnight', match: (h) => h < 6 },
];

/**
 * The local wall-clock times an interval schedule actually sends at, in `tz`.
 * Interval mode is anchored to the UTC half-hour grid (ticks at :05/:35), so
 * without this preview "Once a day" silently means 00:05 UTC — fine at Ningbo
 * (08:05), a surprise elsewhere (review finding, four lenses).
 */
function intervalSendTimes(intervalMinutes: number, tz: string): string[] {
  const now = new Date();
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const out: string[] = [];
  for (let k = 0; k < 1440 / intervalMinutes; k++) {
    const d = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, k * intervalMinutes + 5)
    );
    out.push(fmt.format(d));
  }
  return out.sort();
}

/**
 * Digest cadence editor (BLU-734, reworked after /full-review). Everything is
 * dropdown-constrained so a schedule cannot be mistyped, and the mode control
 * is a Select rather than a radio group on purpose: Radix radios commit on
 * arrow keys, so a stray keystroke would have switched mode and destroyed a
 * configured times list (review blocker, design lens). Mode switches
 * round-trip losslessly: the last-used interval and times list are remembered
 * for the session, so flipping over to peek and back loses nothing.
 *
 * Commits on every deliberate change, like the rest of the section. Times mode
 * seeds with the floor's own timezone (falling back to the browser's, never a
 * hardcoded zone) and a single 08:00 slot. The last remaining time and the
 * add control stay visible but disabled at the 1 and 8 bounds, with the rule
 * stated in the hint.
 */
function DigestScheduleEditor({
  schedule,
  floorTimezone,
  locationName,
  disabled,
  onCommit,
}: {
  schedule: PortlessDigestSchedule;
  floorTimezone: string | null;
  locationName: string;
  disabled: boolean;
  onCommit: (next: PortlessDigestSchedule) => void;
}) {
  // Last-seen config per mode — refs, updated during render, so a mode
  // round-trip restores exactly what the operator had (review blocker fix).
  const lastInterval = useRef<(typeof PORTLESS_DIGEST_INTERVALS)[number]>(30);
  const lastTimes = useRef<{ timezone: string; times: string[] } | null>(null);
  if (schedule.mode === 'interval') {
    lastInterval.current = schedule.intervalMinutes;
  } else {
    lastTimes.current = { timezone: schedule.timezone, times: schedule.times };
  }

  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const previewTz = floorTimezone ?? browserTz;

  const times = schedule.mode === 'times' ? schedule.times : [];
  const timezoneOptions = Array.from(
    new Set(
      [floorTimezone, schedule.mode === 'times' ? schedule.timezone : null, ...COMMON_TIMEZONES].filter(
        (tz): tz is string => !!tz
      )
    )
  );

  const handleModeChange = (mode: string) => {
    if (mode === schedule.mode) return;
    if (mode === 'interval') {
      onCommit({ mode: 'interval', intervalMinutes: lastInterval.current });
    } else {
      onCommit(
        lastTimes.current
          ? { mode: 'times', ...lastTimes.current }
          : { mode: 'times', timezone: floorTimezone ?? browserTz, times: ['08:00'] }
      );
    }
  };

  const zoneLabel = (tz: string): string => {
    const off = gmtOffsetLabel(tz);
    return off ? `${tz.replace(/_/g, ' ')} (${off})` : tz.replace(/_/g, ' ');
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={schedule.mode} disabled={disabled} onValueChange={handleModeChange}>
          <SelectTrigger
            className="h-8 w-40 text-xs"
            aria-label={`Digest schedule mode for ${locationName}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="interval" className="text-xs">
              On an interval
            </SelectItem>
            <SelectItem value="times" className="text-xs">
              At set times
            </SelectItem>
          </SelectContent>
        </Select>

        {schedule.mode === 'interval' && (
          <Select
            value={String(schedule.intervalMinutes)}
            disabled={disabled}
            onValueChange={(v) =>
              onCommit({
                mode: 'interval',
                intervalMinutes: Number(v) as (typeof PORTLESS_DIGEST_INTERVALS)[number],
              })
            }
          >
            <SelectTrigger
              className="h-8 w-44 text-xs"
              aria-label={`Digest interval for ${locationName}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PORTLESS_DIGEST_INTERVALS.map((m) => (
                <SelectItem key={m} value={String(m)} className="text-xs">
                  {intervalLabel(m)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {schedule.mode === 'times' && (
          <Select
            value={schedule.timezone}
            disabled={disabled}
            onValueChange={(timezone) => onCommit({ mode: 'times', timezone, times })}
          >
            <SelectTrigger
              className="h-8 w-56 text-xs"
              aria-label={`Digest timezone for ${locationName}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {timezoneOptions.map((tz) => (
                <SelectItem key={tz} value={tz} className="text-xs">
                  {zoneLabel(tz)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Interval mode is UTC-grid anchored; show where the sends actually land
          once they are sparse enough for the wall-clock time to matter. */}
      {schedule.mode === 'interval' && schedule.intervalMinutes >= 360 && (
        <p className="text-[11px] text-muted-foreground">
          Sends at {intervalSendTimes(schedule.intervalMinutes, previewTz).join(', ')} ({previewTz.replace(/_/g, ' ')} time).
        </p>
      )}

      {schedule.mode === 'times' && (
        <>
          <div className="flex flex-wrap items-center gap-1.5">
            {times.map((t) => (
              <Badge key={t} variant="secondary" className="gap-1 font-mono text-xs">
                {t}
                <button
                  type="button"
                  aria-label={`Remove send time ${t}`}
                  className="flex h-5 w-5 items-center justify-center rounded-sm opacity-60 transition-opacity hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-30 motion-reduce:transition-none"
                  disabled={disabled || times.length <= 1}
                  onClick={() =>
                    onCommit({ mode: 'times', timezone: schedule.timezone, times: times.filter((x) => x !== t) })
                  }
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                </button>
              </Badge>
            ))}
            <Select
              value=""
              disabled={disabled || times.length >= PORTLESS_DIGEST_MAX_TIMES}
              onValueChange={(t) =>
                onCommit({ mode: 'times', timezone: schedule.timezone, times: [...times, t].sort() })
              }
            >
              <SelectTrigger
                className="h-8 w-32 text-xs"
                aria-label={`Add a send time for ${locationName}`}
              >
                <SelectValue placeholder="Add time" />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {SLOT_BANDS.map((band) => {
                  const slots = PORTLESS_DIGEST_TIME_SLOTS.filter(
                    (s) => band.match(Number(s.slice(0, 2))) && !times.includes(s)
                  );
                  if (slots.length === 0) return null;
                  return (
                    <SelectGroup key={band.label}>
                      <SelectLabel>{band.label}</SelectLabel>
                      {slots.map((s) => (
                        <SelectItem key={s} value={s} className="font-mono text-xs">
                          {s}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <p className="text-[11px] text-muted-foreground">
            One to eight times. Digests post about five minutes after each time shown.
          </p>
        </>
      )}
    </div>
  );
}

interface PortlessBatchGateSectionProps {
  /** When true, renders without the Card wrapper for embedding in the Console. */
  embedded?: boolean;
  /** Console single-location mode: render only this location's row. */
  locationId?: string;
}

export function PortlessBatchGateSection({
  embedded = false,
  locationId,
}: PortlessBatchGateSectionProps) {
  const locationsQuery = trpc.productionSettings.getLocationSettings.useQuery();
  const [draftTag, setDraftTag] = useState<Record<string, string>>({});

  const setGateMutation = trpc.productionSettings.setLocationPortlessGate.useMutation({
    onSuccess: (_data, variables) => {
      toast.success(
        variables.digestSchedule
          ? 'Digest schedule updated'
          : variables.readyTags
            ? 'Portless identification tags updated'
            : variables.enabled
              ? 'Portless batch gate enabled'
              : 'Portless batch gate disabled',
        // Stable per-location id: building a 4-slot schedule replaces one
        // toast instead of stacking four (review finding, design lens).
        { id: `portless-gate-${variables.locationId}` }
      );
      locationsQuery.refetch();
    },
    onError: (error, variables) => {
      const what = variables?.digestSchedule
        ? 'digest schedule'
        : variables?.readyTags
          ? 'identification tags'
          : 'Portless batch gate';
      toast.error(`Failed to update ${what}: ${error.message}`, {
        id: `portless-gate-${variables?.locationId ?? 'unknown'}`,
      });
    },
  });

  const isLoading = locationsQuery.isLoading;
  const hasError = locationsQuery.isError;
  const allLocations = locationsQuery.data || [];
  // Only Portless floors — the gate is meaningless anywhere else, and showing it
  // on a StarShipIt or manual location would imply it does something.
  //
  // `shipsPortless` (not `shippingProvider`) because at a 3PL floor the owner may
  // not use Portless at all while a junction client does: Thread Lab owns Ningbo
  // and ships manually, Threadheads ships Portless from it. Filtering on the
  // location's own provider would hide the one floor this setting is for.
  const locations = (locationId ? allLocations.filter((l) => l.id === locationId) : allLocations).filter(
    (l) => l.shipsPortless
  );

  const content = (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Keep orders out of Ready to Batch until Portless has actually issued a shipping label
        (checked against Pipe17 every 10 minutes). They stay visible in All Orders with a badge,
        and return automatically the cycle after the label arrives, so an unshippable order
        never takes a bin.
      </p>

      {hasError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Failed to load locations</AlertTitle>
          <AlertDescription className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => locationsQuery.refetch()}>
              <RefreshCw className="mr-2 h-3 w-3" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading locations…
        </div>
      )}

      {!isLoading && !hasError && locations.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No locations at this organisation ship via Portless, so there is nothing to gate.
        </p>
      )}

      {!isLoading && !hasError && locations.length > 0 && (
        <div className="space-y-3" role="list" aria-label="Per-location Portless batch gate">
          {locations.map((location) => {
            const enabled = location.portlessGateEnabled;
            const tags = location.portlessReadyTags ?? DEFAULT_IDENTIFICATION_TAGS;
            const usingDefaults = location.portlessReadyTags === null;
            const rowPending =
              setGateMutation.isPending && setGateMutation.variables?.locationId === location.id;
            const switchId = `portless-gate-${location.id}`;
            const draft = draftTag[location.id] ?? '';

            const commitTags = (next: string[]) =>
              setGateMutation.mutate({ locationId: location.id, enabled, readyTags: next });

            return (
              <div key={location.id} role="listitem" className="overflow-hidden rounded-lg bg-muted/30">
                <div className="flex items-center justify-between p-3 transition-colors hover:bg-muted/50">
                  <div>
                    {!locationId && <p className="font-medium">{location.name}</p>}
                    <NoteLine icon={PackageX} className="mt-1">
                      {enabled
                        ? 'Unlabelled orders are held out of Ready to Batch'
                        : 'All orders batchable regardless of label state'}
                    </NoteLine>
                  </div>

                  <div className="flex items-center gap-2">
                    {rowPending && (
                      <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" aria-hidden="true" />
                    )}
                    <Label htmlFor={switchId} className="text-xs font-normal text-muted-foreground">
                      Require label
                    </Label>
                    <Switch
                      id={switchId}
                      checked={enabled}
                      onCheckedChange={(next) =>
                        setGateMutation.mutate({ locationId: location.id, enabled: next })
                      }
                      disabled={rowPending}
                      aria-label={`Require a Portless label before batching at ${location.name}`}
                    />
                  </div>
                </div>

                {enabled && (
                  <div className="border-t bg-background/40 p-3">
                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Identification tags</p>
                    <p className="mb-2 text-xs text-muted-foreground">
                      Tags that identify a Portless cross-dock order
                      {usingDefaults && ' (using the default)'}. Used to tell "not yet
                      pushed to Portless" from "awaiting their label" on the Issues page.
                      They don't switch the gate; the toggle above does.
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {tags.map((tag) => (
                        <Badge key={tag} variant="secondary" className="gap-1 font-mono text-xs">
                          {tag}
                          <button
                            type="button"
                            aria-label={`Remove tag ${tag}`}
                            className="rounded-sm opacity-60 transition-opacity hover:opacity-100"
                            disabled={rowPending}
                            onClick={() => commitTags(tags.filter((x) => x !== tag))}
                          >
                            <X className="h-3 w-3" aria-hidden="true" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <Input
                        value={draft}
                        placeholder="portless_ready"
                        aria-label={`Add a readiness tag for ${location.name}`}
                        className="h-8 max-w-56 font-mono text-xs"
                        disabled={rowPending}
                        onChange={(e) =>
                          setDraftTag((d) => ({ ...d, [location.id]: e.target.value }))
                        }
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter') return;
                          e.preventDefault();
                          const v = draft.trim().toLowerCase();
                          if (!v || tags.includes(v)) return;
                          commitTags([...tags, v]);
                          setDraftTag((d) => ({ ...d, [location.id]: '' }));
                        }}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 gap-1"
                        disabled={rowPending || !draft.trim()}
                        onClick={() => {
                          const v = draft.trim().toLowerCase();
                          if (!v || tags.includes(v)) return;
                          commitTags([...tags, v]);
                          setDraftTag((d) => ({ ...d, [location.id]: '' }));
                        }}
                      >
                        <Plus className="h-3 w-3" aria-hidden="true" />
                        Add
                      </Button>
                    </div>
                    <div className="mt-3 border-t pt-3">
                      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Slack digest</p>
                      <p className="mb-2 text-xs text-muted-foreground">
                        Slack digest for blocked orders (silent when there are none;
                        cadence below). A webhook URL is preferred: it works even
                        where the bot can't be invited, such as a shared channel.
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        <Input
                          defaultValue={location.portlessBlockSlackWebhookUrl ?? ''}
                          placeholder="https://hooks.slack.com/services/…"
                          aria-label={`Slack webhook URL for ${location.name}`}
                          className="h-8 max-w-80 text-xs"
                          disabled={rowPending}
                          onBlur={(e) => {
                            const next = e.target.value.trim();
                            if (next === (location.portlessBlockSlackWebhookUrl ?? '')) return;
                            setGateMutation.mutate({
                              locationId: location.id,
                              enabled,
                              slackWebhookUrl: next,
                            });
                          }}
                        />
                        <Input
                          defaultValue={location.portlessBlockSlackChannel ?? ''}
                          placeholder="#channel (fallback)"
                          aria-label={`Slack channel for ${location.name}`}
                          className="h-8 max-w-44 text-xs"
                          disabled={rowPending}
                          onBlur={(e) => {
                            const next = e.target.value.trim();
                            if (next === (location.portlessBlockSlackChannel ?? '')) return;
                            setGateMutation.mutate({
                              locationId: location.id,
                              enabled,
                              slackChannel: next,
                            });
                          }}
                        />
                      </div>
                      {/* Who gets @mentioned on this floor's digest. Saves on
                          change (no blur to wait for) — the picker is a
                          discrete selection, not free text. */}
                      <div className="mt-2">
                        <SlackMentionPicker
                          value={location.portlessBlockMentionUserIds}
                          disabled={rowPending}
                          hint="Tagged at the top of this floor's blocked-order digest."
                          onChange={(mentionUserIds) =>
                            setGateMutation.mutate({
                              locationId: location.id,
                              enabled,
                              mentionUserIds,
                            })
                          }
                        />
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          Needs Slack DMs (Threadbot) connected. Teammates are matched
                          to Slack by their PrintRun email.
                        </p>
                      </div>
                      {/* BLU-734: when the digest may post. Missed windows lose
                          nothing — each digest carries everything queued since
                          the previous send. */}
                      <div className="mt-3 border-t pt-3">
                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Send schedule</p>
                        <p className="mb-2 text-xs text-muted-foreground">
                          When to send it: pick a rhythm or exact times. An
                          unsent order is included in the next digest either way.
                        </p>
                        <DigestScheduleEditor
                          schedule={location.portlessDigestSchedule}
                          floorTimezone={location.timezone}
                          locationName={location.name}
                          disabled={rowPending}
                          onCommit={(digestSchedule) =>
                            setGateMutation.mutate({
                              locationId: location.id,
                              enabled,
                              digestSchedule,
                            })
                          }
                        />
                        {!location.portlessBlockSlackWebhookUrl && !location.portlessBlockSlackChannel && (
                          <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-400">
                            Add a webhook URL or channel above to start sending.
                          </p>
                        )}
                      </div>
                    </div>

                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div role="status" aria-live="polite" className="sr-only">
        {setGateMutation.isSuccess && 'Portless batch gate updated'}
      </div>
    </div>
  );

  if (embedded) return content;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <PackageX className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          <CardTitle className="text-lg">Portless label gate</CardTitle>
        </div>
        <CardDescription>
          Stop orders being batched before Portless has issued a shipping label.
        </CardDescription>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}
